import { MilestoneToBeBilled } from '@/services/billing/types/billing.type';
import {
  CreateDiscountRequest,
  CreateSalesOrderRequest,
  SalesOrderItem,
} from '@/services/billing/types/create-billing.type';
import { ChoicesOrderUnitMeasure } from '../../orders/types';
import {
  SecondStepFormValues,
  TotalMustBeBilled,
} from '../components/form/billing-order-new-edit-secondstep-form';
import { LegalEntityDistribution } from '../store/billing.store';
import { GroupedUniqueItem } from './list-data.utils';

export const getQuantityFromMilestones = (milestones: MilestoneToBeBilled[]) => {
  return milestones.length > 0 ? milestones[0].orderItem.quantity : 0;
};

export const formatSalesOrder = (
  formValues: SecondStepFormValues[],
  totalMustBeBilled: TotalMustBeBilled[],
  discounts: CreateDiscountRequest[]
): CreateSalesOrderRequest[] => {
  // Calculate the total discount amount from all discount requests
  const globalTotalDiscount = discounts.reduce((sum, discount) => sum + discount.amount, 0);

  // Calculate the total net amount to be billed across all items
  const globalTotalNet = totalMustBeBilled.reduce((sum, item) => sum + item.total, 0);

  const salesOrderRequest: CreateSalesOrderRequest[] = formValues.map((formValue) => {
    let salesOrderSubAmount = 0;
    let salesOrderTotalAmount = 0;
    let salesOrderTotalDiscount = 0;

    const salesOrderItem: SalesOrderItem[] = formValue.items.map((item) => {
      let itemDiscount = 0;

      // Distribute the global discount proportionally based on the item's amount relative to the global net total
      if (globalTotalNet !== 0) {
        itemDiscount = (item.amount / globalTotalNet) * globalTotalDiscount;
      }

      // Calculate the sub-amount (original amount before discount)
      const itemSubAmount = item.amount + itemDiscount;

      // Accumulate totals for the sales order
      salesOrderSubAmount += itemSubAmount;
      salesOrderTotalAmount += item.amount;
      salesOrderTotalDiscount += itemDiscount;

      return {
        value: item.value,
        amount: item.amount,
        totalDiscount: itemDiscount,
        subAmount: itemSubAmount,
        subscription: item.itemInfo.subscription,
        milestones: item.itemInfo.milestones.map((milestone) => milestone._id),
        unitMeasure: item.unitMeasure,
      };
    });

    const documents = formValue.documents?.length ? formValue.documents : [];
    return {
      _id: formValue.legalEntity._id,
      documents: documents,
      subAmount: salesOrderSubAmount,
      totalAmount: salesOrderTotalAmount,
      totalDiscount: salesOrderTotalDiscount,
      items: salesOrderItem,
    };
  });

  return salesOrderRequest;
};

export const formatFormValues = (
  legalEntityDistributions: LegalEntityDistribution[],
  groupedUniqueItems: GroupedUniqueItem[]
) => {
  const legalEntityCount = legalEntityDistributions.length;

  const formValuesItems = groupedUniqueItems.map((item) => {
    const itemId = `${item.subscription}-${item.type}`;

    const quantity = getQuantityFromMilestones(item.milestones);
    const unitMeasure = ChoicesOrderUnitMeasure.amount;

    const distribuitedAmount =
      item.milestones.reduce((acc, milestone) => acc + milestone.total, 0) / legalEntityCount;

    return {
      itemId,
      value: distribuitedAmount,
      amount: distribuitedAmount,
      quantity,
      unitMeasure,
      itemInfo: item,
    };
  });

  return legalEntityDistributions.map(({ legalEntity }) => {
    return {
      legalEntity,
      items: formValuesItems,
    };
  });
};

export const getTotalMustBeBilled = (groupedUniqueItems: GroupedUniqueItem[]) => {
  return groupedUniqueItems.map((item) => {
    const total = item.milestones.reduce((acc, milestone) => acc + milestone.total, 0);
    const itemId = `${item.subscription}-${item.type}`;
    return {
      itemId,
      total,
    };
  });
};
